'use client';

import { Menu } from 'lucide-react';
import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '@repo/ux/navigation-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ux/sheet';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

import { ModeToggle } from './page-mode-toggle';

interface RouteProps {
  href: string;
  label: string;
}

const routeList: RouteProps[] = [
  { href: '/#command', label: 'Command' },
  { href: '/#medical-care', label: 'Medical Care' },
  { href: '/#features', label: 'Features' },
  { href: '/#faq', label: 'FAQ' },
];

interface NavBarProps {
  children?: React.ReactNode;
  isAuthenticated?: boolean;
}

const NavBar = ({ children, isAuthenticated = false }: NavBarProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <header className="sticky border-b-[1px] top-0 z-40 w-full bg-brand-header dark:border-b-slate-700 print:hidden">
      <NavigationMenu className="mx-auto">
        <NavigationMenuList className="container h-14 px-4 w-screen flex justify-between">
          <NavigationMenuItem className="font-bold flex">
            <Link
              rel="noreferrer noopener"
              href="/"
              className="ml-2 font-bold text-xl flex"
            >
              <Image
                src="/assets/images/logo.webp"
                alt="Logo"
                width={100}
                height={100}
                className="h-auto w-auto"
                loading="eager"
              />
            </Link>
          </NavigationMenuItem>

          {children ? (
            <>
              {/* Authenticated mobile */}
              <NavigationMenuItem className="flex md:hidden">
                <ModeToggle />
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                  <SheetTrigger className="px-2" aria-label="Open menu">
                    <Menu className="h-5 w-5 text-white" />
                  </SheetTrigger>
                  <SheetContent
                    side={'left'}
                    className="bg-brand-header border-brand-header"
                  >
                    <SheetHeader>
                      <SheetTitle className="font-bold text-xl text-white">
                        Menu
                      </SheetTitle>
                    </SheetHeader>
                    <nav className="flex flex-col items-start gap-1 mt-4 px-2">
                      {/* {routeList.map(({ href, label }) => (
                        <a
                          rel="noreferrer noopener"
                          key={label}
                          href={href}
                          onClick={() => setIsOpen(false)}
                          className={cn(
                            buttonVariants({ variant: 'ghost' }),
                            'text-white hover:bg-white/10 hover:text-white w-full justify-start'
                          )}
                        >
                          {label}
                        </a>
                      ))} */}
                      <div className="border-t border-white/20 w-full my-2" />
                      {children}
                    </nav>
                  </SheetContent>
                </Sheet>
              </NavigationMenuItem>

              {/* Authenticated desktop */}
              <NavigationMenuItem className="hidden md:flex items-center gap-2">
                {children}
                <ModeToggle />
              </NavigationMenuItem>
            </>
          ) : (
            <>
              {/* Unauthenticated mobile */}
              <NavigationMenuItem className="flex md:hidden">
                <ModeToggle />
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                  <SheetTrigger className="px-2" aria-label="Open menu">
                    <Menu className="h-5 w-5 text-white" />
                  </SheetTrigger>
                  <SheetContent side={'left'}>
                    <SheetHeader>
                      <SheetTitle className="font-bold text-xl">
                        Menu
                      </SheetTitle>
                    </SheetHeader>
                    <nav className="flex flex-col justify-center items-center gap-2 mt-4">
                      {routeList.map(({ href, label }) => (
                        <a
                          rel="noreferrer noopener"
                          key={label}
                          href={href}
                          onClick={() => setIsOpen(false)}
                          className={buttonVariants({ variant: 'ghost' })}
                        >
                          {label}
                        </a>
                      ))}
                      {isAuthenticated ? (
                        <Link
                          href="/participant/dashboard"
                          className={`border ${buttonVariants({ variant: 'outline' })}`}
                        >
                          Dashboard
                        </Link>
                      ) : (
                        <>
                          <Link
                            href="/participant/login"
                            className={`border ${buttonVariants({ variant: 'outline' })}`}
                          >
                            Login
                          </Link>
                          <a
                            href="https://classic.iracesafe.com/faq?source=sportzpeak"
                            className={`border ${buttonVariants({ variant: 'outline' })}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Help Center
                          </a>
                        </>
                      )}
                    </nav>
                  </SheetContent>
                </Sheet>
              </NavigationMenuItem>

              {/* Unauthenticated desktop */}
              <NavigationMenuItem className="hidden md:flex gap-2">
                {routeList.map((route, i) => (
                  <a
                    rel="noreferrer noopener"
                    href={route.href}
                    key={i}
                    className={cn(
                      `${buttonVariants({ variant: 'ghost' })} hover:bg-accent/50`,
                      'text-white'
                    )}
                  >
                    {route.label}
                  </a>
                ))}
              </NavigationMenuItem>

              <NavigationMenuItem className="hidden md:flex items-center gap-2">
                {isAuthenticated ? (
                  <Link
                    href="/participant/dashboard"
                    className={`${buttonVariants({ variant: 'ghost' })} hover:bg-accent/50 border border-white text-white`}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/participant/login"
                      className={`${buttonVariants({ variant: 'ghost' })} hover:bg-accent/50 border border-white text-white`}
                    >
                      Login
                    </Link>
                    <a
                      href="https://classic.iracesafe.com/faq?source=sportzpeak"
                      className={`${buttonVariants({ variant: 'ghost' })} hover:bg-accent/50 text-white`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Help Center
                    </a>
                  </>
                )}
                <ModeToggle />
              </NavigationMenuItem>
            </>
          )}
        </NavigationMenuList>
      </NavigationMenu>
    </header>
  );
};

export default NavBar;
